import { Add as AddIcon, ArrowDownward as ArrowDownIcon, ArrowUpward as ArrowUpIcon, Delete as DeleteIcon, Save as SaveIcon, UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React, { memo, useCallback, useMemo, useEffect, useRef, useState } from 'react';

import { ScenarioWell, scenarioWellsApi, wellsApi } from '@/api/scenarioWells';
import { scenarioLayersApi, ScenarioLayer, type BaseWellCurvesResponse, type BaseWellCurvesProgress } from '@/api/scenarioLayers';
import { scenariosApi } from '@/api/scenarios';
import { LayerDrillingPlanPanel } from '@/components/Scenarios/LayerDrillingPlanPanel';
import { ImportedScenarioWellRow, ScenarioWellsImportCsvModal } from '@/components/Scenarios/ScenarioWellsImportCsvModal';
import {
  MaterialBalanceWellsSection,
  type MbWellRow,
} from '@/components/Scenarios/MaterialBalanceWellsSection';
import {
  contourColorForIndex,
  WellsFieldMap,
  type LatLngTuple,
  type WellsFieldMapContour,
} from '@/components/Scenarios/WellsFieldMap';
import { SCENARIO_UNITS } from '@/types/scenario';
import type {
  MaterialBalanceResult,
  MbContourOrigin,
  MbMlStartDefaults,
} from '@/types/materialBalance';
import type { ScenarioWellBulkUpdateItem } from '@/types/scenarioWell';
import { parsePointCoordinate } from '@/utils/fieldCoordinate';
import {
  isValidGeoOrigin,
  latLngToLocalKm,
  localKmToLatLng,
  reprojectLocalKm,
  resolveContourOrigin,
} from '@/utils/localKmProjection';
import {
  countGroupedWellsCreate,
  ensureLayerCreateBatch,
  type ScenarioWellLayerCreateBatch,
} from '@/utils/scenarioWellsBulk';
import {
  applyMlStartDefaultsToSeed,
  emptyMbPropsForm,
  hasSavedStartLiquidRate,
  mbPropsFormsEqual,
  mbPropsOverridesFromForm,
  mergeMbPropsForm,
  seedMbPropsFromLayer,
  type MbPropsForm,
} from '@/utils/materialBalanceProps';
import type { AutoPlaceResult } from '@/utils/materialBalanceAutoPlace';
import type { MbOptimizeProposal } from '@/utils/materialBalanceOptimize';
import { DEFAULT_WELL_TYPE_RATIO, normalizeWellTypeRatio } from '@/utils/wellInjectionRatio';
import { buildYearWellEntrySchedule } from '@/utils/wellYearSchedule';
import type { MbAutoPlaceConfig } from '@/types/materialBalance';
import type { SeedWellRow } from '@/utils/materialBalanceSeedManual';

type MbPropsLayerState = {
  form: MbPropsForm;
  saved: MbPropsForm;
  seedNote: string | null;
  krTable: number[][];
  injWells: Record<string, Record<string, number>>;
  lastResult: MaterialBalanceResult | null;
  autoPlace: MbAutoPlaceConfig | null;
};

type ContourLayerState = {
  /** Geographic lat/lng for Leaflet display (projected from local km via origin). */
  vertices: LatLngTuple[];
  saved: LatLngTuple[];
  /** Fixed geographic datum; contour + wells are km relative to this. */
  origin: MbContourOrigin | null;
  savedOrigin: MbContourOrigin | null;
};

function parseContourVerticesFromConfig(
  config: { contour?: { x: number; y: number }[]; contour_origin?: MbContourOrigin | null },
  scenarioCoordinate: string | null,
): ContourLayerState {
  const pts = (config.contour || []).map((p) => ({
    x: Number(p.x),
    y: Number(p.y),
  }));
  const scenarioPt = parsePointCoordinate(scenarioCoordinate);
  // Stored contour is always local km relative to contour_origin (or scenario pin fallback).
  const origin = resolveContourOrigin({
    saved: config.contour_origin ?? null,
    scenario: scenarioPt,
  });
  const vertices: LatLngTuple[] = pts.map((p) => {
    const g = localKmToLatLng(p.x, p.y, origin);
    return [g.lat, g.lng] as LatLngTuple;
  });
  const savedOrigin = isValidGeoOrigin(config.contour_origin) ? config.contour_origin : origin;

  return { vertices, saved: vertices, origin: savedOrigin, savedOrigin };
}

/** Temporary: hide Interactive / Table until the new Wells layout is filled in. */
const SHOW_LEGACY_WELLS_UI = false;

interface ScenarioWellsStepProps {
  scenarioId: string;
  onWellsChange?: (wells: ScenarioWell[]) => void;
  onGoToEconomy?: () => void;
  isManualMode?: boolean;
  /** Parent can call the latest handler to flush unsaved wells before leaving edit mode */
  onRegisterPendingSave?: (handler: (() => Promise<boolean>) | null) => void;
}

interface WellFormData {
  id: string; // Unique key: server scenario_well_id or temp-xxx for new
  serverId: string | null; // scenario_well_id from server; null for unsaved wells
  well_name: string;
  entry_date: Date | null;
  /** Shut-down calendar year (MB t1); default 2100 when unset */
  end_year: number;
  scenario_layer_id: string | null;
  type_id: string | null;
  x_km: number | null;
  y_km: number | null;
  /** Optional startup rate t/d (stored as ini_liquid); blank = field default */
  q_start_td: number | null;
  isInvalid?: boolean; // true if layer doesn't exist
}

/** Snapshot of well fields for dirty check (keyed by serverId) */
interface WellSnapshot {
  well_name: string;
  scenario_layer_id: string;
  type_id?: string;
  entry_date?: string;
  end_year?: number;
  ini_oil?: number;
  ini_gas?: number;
  ini_liquid?: number;
  x_km?: number | null;
  y_km?: number | null;
}

const MB_DEFAULT_END_YEAR = 2100;

function entryDateFromYear(year: number): Date {
  return new Date(year, 0, 1);
}

function formatEntryDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type WellsInnerTab = 'manual' | 'interactive';
const INITIAL_INTERACTIVE_YEARS = 14;
const MAX_INTERACTIVE_YEARS = 50;

type InteractiveLayerPlan = {
  startYear: number;
  wellsPerYear: number[];
  injectionWellsPerYear: number[];
  injectionRatio?: { injection: number; production: number };
  injectionRatioLinked?: boolean;
};

function createDefaultInteractiveLayerPlan(startYear = new Date().getFullYear()): InteractiveLayerPlan {
  return {
    startYear,
    wellsPerYear: Array(INITIAL_INTERACTIVE_YEARS).fill(0),
    injectionWellsPerYear: Array(INITIAL_INTERACTIVE_YEARS).fill(0),
    injectionRatio: { ...DEFAULT_WELL_TYPE_RATIO },
    injectionRatioLinked: false,
  };
}

function parseWellEntryYear(entryDate?: string | null): number | null {
  if (!entryDate || typeof entryDate !== 'string') {
    return null;
  }
  const year = Number(entryDate.slice(0, 4));
  return Number.isFinite(year) ? year : null;
}

function isProductionWell(well: ScenarioWell, productionTypeId: string | null): boolean {
  if (productionTypeId && well.type_id === productionTypeId) {
    return true;
  }
  const typeName = (well.well_type?.name || well.well?.type || '').toLowerCase();
  return typeName === 'production' || typeName === 'debit' || typeName === 'добывающая';
}

function isInjectionWell(well: ScenarioWell, injectionTypeId: string | null): boolean {
  if (injectionTypeId && well.type_id === injectionTypeId) {
    return true;
  }
  const typeName = (well.well_type?.name || well.well?.type || '').toLowerCase();
  return typeName === 'injection' || typeName === 'нагнетательная';
}

function buildCountsPerYear(
  wells: ScenarioWell[],
  startYear: number,
  span: number,
): number[] {
  const yearCounts = new Map<number, number>();
  wells.forEach((well) => {
    const year = parseWellEntryYear(well.entry_date);
    if (year == null) {
      return;
    }
    yearCounts.set(year, (yearCounts.get(year) ?? 0) + 1);
  });
  return Array.from({ length: span }, (_, i) => yearCounts.get(startYear + i) ?? 0);
}

function inferWellTypeIdFromName(
  wellName: string,
  productionTypeId: string | null,
  injectionTypeId: string | null,
): string | null {
  if (injectionTypeId && /_inj(?:_|$)/i.test(wellName)) {
    return injectionTypeId;
  }
  return productionTypeId;
}

function resolveWellFormTypeId(
  well: ScenarioWell,
  productionTypeId: string | null,
  injectionTypeId: string | null,
): string | null {
  if (well.type_id) {
    return well.type_id;
  }
  if (isInjectionWell(well, injectionTypeId) && injectionTypeId) {
    return injectionTypeId;
  }
  if (isProductionWell(well, productionTypeId) && productionTypeId) {
    return productionTypeId;
  }
  return inferWellTypeIdFromName(
    well.well_name || well.well?.name || '',
    productionTypeId,
    injectionTypeId,
  );
}

function apiErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'detail' in err) {
    const d = (err as { detail?: unknown }).detail;
    if (typeof d === 'string') return d;
    if (d != null) return String(d);
  }
  return 'Failed to load base well curves';
}

/**
 * Synthetic single-well decline (oil + liquid) for local dev without API.
 * Kept commented out; Interactive tab uses `scenarioLayersApi.getBaseWellCurves`.
 *
 * To use again, uncomment this function and in the `wellsInnerTab === 'interactive'`
 * effect replace the `Promise.allSettled` block with:
 *   scenarioLayers.forEach((l) => {
 *     curves[l.scenario_layer_id] = makeSyntheticBaseWellCurves(l.scenario_layer_id, l.layer_name);
 *   });
 */
/*
function makeSyntheticBaseWellCurves(scenarioLayerId: string, layerName?: string | null): BaseWellCurvesResponse {
  const n = 50;
  let seed = 0;
  for (let k = 0; k < scenarioLayerId.length; k++) {
    seed = (seed + scenarioLayerId.charCodeAt(k) * (k + 1)) % 997;
  }
  const peakScale = 0.85 + (seed % 30) / 100;
  const oil_rate: number[] = [];
  const liquid_rate: number[] = [];
  const water_cut_pct: number[] = [];
  for (let i = 0; i < n; i++) {
    const qo = peakScale * (120 * Math.exp(-i / 18) + 1.5);
    const liqMult = 1.08 + 0.35 * (i / n);
    const ql = qo * liqMult;
    const wc = ql > 1e-9 ? Math.min(100, Math.max(0, ((ql - qo) / ql) * 100)) : 0;
    oil_rate.push(qo);
    liquid_rate.push(ql);
    water_cut_pct.push(wc);
  }
  return {
    scenario_layer_id: scenarioLayerId,
    layer_name: layerName ?? undefined,
    oil_rate,
    liquid_rate,
    water_cut_pct,
  };
}
*/

export const ScenarioWellsStep: React.FC<ScenarioWellsStepProps> = memo(({
  scenarioId,
  onWellsChange,
  onGoToEconomy: _onGoToEconomy,
  isManualMode = false,
  onRegisterPendingSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // All wells in editable state
  const [wellsData, setWellsData] = useState<WellFormData[]>([]);
  // IDs of wells removed by user (to send in items_to_delete on save)
  const [pendingDeletes, setPendingDeletes] = useState<string[]>([]);
  // Last saved/server state per serverId — only changed wells go to items_to_update
  const [originalByServerId, setOriginalByServerId] = useState<Record<string, WellSnapshot>>({});

  // Data for creating wells
  const [scenarioLayers, setScenarioLayers] = useState<ScenarioLayer[]>([]);
  const [productionWellTypeId, setProductionWellTypeId] = useState<string | null>(null);
  const [injectionWellTypeId, setInjectionWellTypeId] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  const [wellsInnerTab, setWellsInnerTab] = useState<WellsInnerTab>('interactive');
  const [savedScenarioWells, setSavedScenarioWells] = useState<ScenarioWell[]>([]);
  const [interactivePlanByLayerId, setInteractivePlanByLayerId] = useState<Record<string, InteractiveLayerPlan>>({});
  const [interactiveDirty, setInteractiveDirty] = useState(false);
  const [interactiveSaveConfirmOpen, setInteractiveSaveConfirmOpen] = useState(false);
  const [mapLayerId, setMapLayerId] = useState('');
  const [contourByLayerId, setContourByLayerId] = useState<Record<string, ContourLayerState>>({});
  const [propsByLayerId, setPropsByLayerId] = useState<Record<string, MbPropsLayerState>>({});
  /** Per-layer ML (or manual) predicted start liquid rate / WC for MB + new wells. */
  const [mlStartByLayerId, setMlStartByLayerId] = useState<Record<string, MbMlStartDefaults>>({});
  const [scenarioCoordinate, setScenarioCoordinate] = useState<string | null>(null);
  const [contourSaving, setContourSaving] = useState(false);
  const [contourError, setContourError] = useState<string | null>(null);
  const [selectedWellId, setSelectedWellId] = useState<string | null>(null);
  const [mbWellsSaving, setMbWellsSaving] = useState(false);
  const [mbPropsSaving, setMbPropsSaving] = useState(false);
  const [mbRunningLayerId, setMbRunningLayerId] = useState<string | null>(null);
  const [mbRunErrorByLayerId, setMbRunErrorByLayerId] = useState<Record<string, string | null>>({});
  const [baseCurvesCache, setBaseCurvesCache] = useState<{
    key: string;
    curves: Record<string, BaseWellCurvesResponse>;
    errors: Record<string, string>;
  } | null>(null);
  const [loadingBaseCurves, setLoadingBaseCurves] = useState(false);
  const [baseCurvesProgress, setBaseCurvesProgress] = useState<string | null>(null);
  const [scenarioUnitId, setScenarioUnitId] = useState<number>(SCENARIO_UNITS.TONS);
  const [scenarioMlBindingKey, setScenarioMlBindingKey] = useState('default');
  const [importModalOpen, setImportModalOpen] = useState(false);

  const layersCacheKey = useMemo(
    () => `${scenarioMlBindingKey}\n${scenarioLayers.map((l) => l.scenario_layer_id).sort().join('\n')}`,
    [scenarioLayers, scenarioMlBindingKey],
  );

  const featureWarningMessages = useMemo(() => {
    if (!baseCurvesCache?.curves) return [] as string[];
    const msgs: string[] = [];
    for (const layer of scenarioLayers) {
      const curve = baseCurvesCache.curves[layer.scenario_layer_id];
      const warnings = curve?.feature_warnings?.messages;
      if (warnings?.length) {
        warnings.forEach((m) => msgs.push(`${layer.layer_name}: ${m}`));
      }
    }
    return msgs;
  }, [baseCurvesCache, scenarioLayers]);

  const plannedInteractiveWellCount = useMemo(() => {
    let total = 0;
    scenarioLayers.forEach((layer) => {
      const layerPlan = interactivePlanByLayerId[layer.scenario_layer_id];
      if (!layerPlan) return;
      layerPlan.wellsPerYear.forEach((countRaw) => {
        total += Math.max(0, Math.floor(countRaw || 0));
      });
      layerPlan.injectionWellsPerYear.forEach((countRaw) => {
        total += Math.max(0, Math.floor(countRaw || 0));
      });
    });
    return total;
  }, [scenarioLayers, interactivePlanByLayerId]);

  // Table filters and sort (display only)
  const [filterName, setFilterName] = useState('');
  const [filterDate, setFilterDate] = useState<Date | null>(null);
  const [filterLayerId, setFilterLayerId] = useState<string>('');
  type SortColumn = 'name' | 'reservoir' | 'date';
  const [sortBy, setSortBy] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const filteredAndSortedWells = useMemo(() => {
    let list = wellsData.filter((well) => {
      if (filterName.trim()) {
        const name = (well.well_name || '').toLowerCase();
        if (!name.includes(filterName.trim().toLowerCase())) return false;
      }
      if (filterDate) {
        const wellDate = well.entry_date;
        if (!wellDate) return false;
        const d = wellDate instanceof Date ? wellDate : new Date(wellDate);
        if (d.getFullYear() !== filterDate.getFullYear() || d.getMonth() !== filterDate.getMonth() || d.getDate() !== filterDate.getDate()) return false;
      }
      if (filterLayerId) {
        if (well.scenario_layer_id !== filterLayerId) return false;
      }
      return true;
    });
    if (sortBy) {
      const layerNameById = Object.fromEntries(scenarioLayers.map((l) => [l.scenario_layer_id, l.layer_name ?? '']));
      const toDateTs = (d: Date | null) => (d ? (d instanceof Date ? d : new Date(d)).getTime() : 0);
      list = [...list].sort((a, b) => {
        let cmp = 0;
        if (sortBy === 'name') {
          cmp = (a.well_name || '').localeCompare(b.well_name || '', undefined, { sensitivity: 'base' });
        } else if (sortBy === 'reservoir') {
          cmp = (layerNameById[a.scenario_layer_id ?? ''] ?? '').localeCompare(layerNameById[b.scenario_layer_id ?? ''] ?? '', undefined, { sensitivity: 'base' });
        } else {
          cmp = toDateTs(a.entry_date) - toDateTs(b.entry_date);
        }
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [wellsData, scenarioLayers, filterName, filterDate, filterLayerId, sortBy, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortBy(null);
        setSortDirection('asc');
      }
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortHeaderSx = {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    color: 'primary.main',
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    '&:hover': { color: 'primary.dark' },
  };

  const normalizeLayerPrefix = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return 'Reservoir';
    return trimmed.replace(/\s+/g, '_');
  };

  const buildInteractivePlansFromSavedWells = (
    layers: ScenarioLayer[],
    wells: ScenarioWell[],
    productionTypeId: string | null,
    injectionTypeId: string | null,
  ): Record<string, InteractiveLayerPlan> => {
    const nowYear = new Date().getFullYear();
    const plans: Record<string, InteractiveLayerPlan> = {};

    layers.forEach((layer) => {
      const layerId = layer.scenario_layer_id;
      const layerWells = wells.filter((w) => w.scenario_layer_id === layerId);
      const productionWells = layerWells.filter((w) => isProductionWell(w, productionTypeId));
      const injectionWells = layerWells.filter((w) => isInjectionWell(w, injectionTypeId));

      const allYears = layerWells
        .map((w) => parseWellEntryYear(w.entry_date))
        .filter((y): y is number => y != null);

      const startYear = allYears.length > 0 ? Math.min(...allYears) : nowYear;
      const maxYear = allYears.length > 0 ? Math.max(...allYears) : startYear;
      const rawSpan = Math.max(INITIAL_INTERACTIVE_YEARS, maxYear - startYear + 1);
      const span = Math.min(MAX_INTERACTIVE_YEARS, rawSpan);

      plans[layerId] = {
        startYear,
        wellsPerYear: buildCountsPerYear(productionWells, startYear, span),
        injectionWellsPerYear: buildCountsPerYear(injectionWells, startYear, span),
        injectionRatio: { ...DEFAULT_WELL_TYPE_RATIO },
        injectionRatioLinked: false,
      };
    });

    return plans;
  };

  const applyServerWellsState = (
    scenarioWells: ScenarioWell[],
    layers: ScenarioLayer[],
    productionTypeId: string | null,
    injectionTypeId: string | null,
  ) => {
    setSavedScenarioWells(scenarioWells);
    onWellsChange?.(scenarioWells);
    setPendingDeletes([]);

    const nextOriginals: Record<string, WellSnapshot> = {};
    scenarioWells.forEach(well => {
      const sid = well.scenario_well_id || well.id;
      const endYear = well.end_year ?? MB_DEFAULT_END_YEAR;
      nextOriginals[sid] = {
        well_name: well.well_name || well.well?.name || '',
        scenario_layer_id: well.scenario_layer_id || '',
        type_id: well.type_id,
        entry_date: typeof well.entry_date === 'string' ? well.entry_date : undefined,
        end_year: endYear,
        ini_oil: well.ini_oil ?? 0,
        ini_gas: well.ini_gas ?? 0,
        ini_liquid: well.ini_liquid ?? 0,
        x_km: well.x_km ?? null,
        y_km: well.y_km ?? null,
      };
    });
    setOriginalByServerId(nextOriginals);

    const updatedWellsFormData: WellFormData[] = scenarioWells.map(well => {
      const sid = well.scenario_well_id || well.id;
      const layerExists = layers.some(l => l.scenario_layer_id === well.scenario_layer_id);
      let entryDate: Date | null = null;
      if (well.entry_date) {
        const dateStr = well.entry_date;
        if (typeof dateStr === 'string') {
          const [y, m, d] = dateStr.split('-').map(Number);
          entryDate = new Date(y, m - 1, d);
        } else if (dateStr && typeof dateStr === 'object' && 'getTime' in dateStr) {
          entryDate = dateStr as Date;
        }
      }
      const qStart =
        well.ini_liquid != null && Number(well.ini_liquid) > 0 ? Number(well.ini_liquid) : null;
      return {
        id: sid,
        serverId: sid,
        well_name: well.well_name || well.well?.name || '',
        entry_date: entryDate,
        end_year: well.end_year ?? MB_DEFAULT_END_YEAR,
        scenario_layer_id: well.scenario_layer_id || null,
        type_id: resolveWellFormTypeId(well, productionTypeId, injectionTypeId),
        x_km: well.x_km ?? null,
        y_km: well.y_km ?? null,
        q_start_td: qStart,
        isInvalid: !layerExists,
      };
    });
    setWellsData(updatedWellsFormData);

    setInteractivePlanByLayerId(buildInteractivePlansFromSavedWells(layers, scenarioWells, productionTypeId, injectionTypeId));
    setInteractiveDirty(false);
  };

  // Update well validity when layers change
  useEffect(() => {
    if (scenarioLayers.length > 0 && wellsData.length > 0) {
      setWellsData(prev =>
        prev.map(well => ({
          ...well,
          isInvalid: well.scenario_layer_id ? !scenarioLayers.some(l => l.scenario_layer_id === well.scenario_layer_id) : true,
        }))
      );
    }
  }, [scenarioLayers]);

  // Load scenario wells and required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load scenario layers and well types first
        setLoadingData(true);
        const [layers, wellTypes, scenario] = await Promise.all([
          scenarioLayersApi.getScenarioLayers(scenarioId),
          wellsApi.getWellTypes(),
          scenariosApi.getScenario(scenarioId),
        ]);

        setScenarioLayers(layers);
        setMapLayerId((prev) => prev || layers[0]?.scenario_layer_id || '');
        setScenarioCoordinate(scenario.coordinate ?? null);
        setScenarioUnitId(scenario.unit_id ?? SCENARIO_UNITS.TONS);
        const mlMode = scenario.ml_mode === 'custom' || scenario.ml_mode === 'analogue'
          ? scenario.ml_mode
          : 'default';
        let analogueKey = '';
        if (mlMode === 'analogue') {
          try {
            const modelState = await scenariosApi.getModelState(scenarioId);
            analogueKey = (modelState.analogue_layers || [])
              .map((r) => `${r.scenario_layer_id}:${r.status}:${r.updated_at ?? ''}:${r.n_matches}`)
              .sort()
              .join('|');
          } catch {
            analogueKey = 'unknown';
          }
        }
        setScenarioMlBindingKey(`${mlMode}:${scenario.custom_model_id ?? ''}:${analogueKey}`);

        const productionType = wellTypes.find(
          type => type.name.toLowerCase() === 'production' ||
                  type.name.toLowerCase() === 'debit' ||
                  type.name.toLowerCase() === 'добывающая'
        ) || wellTypes[0];
        const injectionType = wellTypes.find(
          type => type.name.toLowerCase() === 'injection' ||
                  type.name.toLowerCase() === 'нагнетательная'
        ) || null;

        if (productionType) {
          setProductionWellTypeId(productionType.id);
        }
        if (injectionType) {
          setInjectionWellTypeId(injectionType.id);
        }

        // Load scenario wells
        const scenarioWells = await scenarioWellsApi.getScenarioWells(scenarioId);
        applyServerWellsState(
          scenarioWells,
          layers,
          productionType?.id ?? null,
          injectionType?.id ?? null,
        );
      } catch (err: any) {
        console.error('Failed to load data:', err);
        const detail = err?.detail || err?.response?.data?.detail || err?.message;
        setError(detail ? `Failed to load data: ${detail}` : 'Failed to load data');
      } finally {
        setLoading(false);
        setLoadingData(false);
      }
    };

    if (scenarioId) {
      loadData();
    }
  }, [scenarioId, onWellsChange]);

  useEffect(() => {
    setWellsInnerTab('interactive');
    setBaseCurvesCache(null);
    setInteractiveDirty(false);
  }, [scenarioId]);

  const contourLayersKey = useMemo(
    () => scenarioLayers.map((l) => l.scenario_layer_id).join('\n'),
    [scenarioLayers],
  );

  useEffect(() => {
    if (!scenarioId || scenarioLayers.length === 0) {
      setContourByLayerId({});
      setPropsByLayerId({});
      setMlStartByLayerId({});
      return;
    }
    let cancelled = false;
    const layersSnapshot = scenarioLayers;
    (async () => {
      setContourError(null);
      try {
        const [layerEntries, mlDefaultsRes] = await Promise.all([
          Promise.all(
            layersSnapshot.map(async (layer) => {
              const [res, volumetric] = await Promise.all([
                scenariosApi.getMaterialBalance(scenarioId, layer.scenario_layer_id),
                scenarioLayersApi.getVolumetricEstimate(layer.scenario_layer_id).catch(() => null),
              ]);
              return { layer, res, volumetric };
            }),
          ),
          scenariosApi.getMaterialBalanceMlStartDefaults(scenarioId).catch(() => ({ layers: [] })),
        ]);
        if (cancelled) return;

        const mlById: Record<string, MbMlStartDefaults> = {};
        for (const row of mlDefaultsRes.layers || []) {
          mlById[row.scenario_layer_id] = row;
        }
        setMlStartByLayerId(mlById);

        const entries = layerEntries.map(({ layer, res, volumetric }) => {
          const ml = mlById[layer.scenario_layer_id];
          const seed = applyMlStartDefaultsToSeed(
            seedMbPropsFromLayer(layer, volumetric),
            ml,
          );
          const seededKeys = Object.keys(seed);
          const savedProps = res.config?.props || {};
          const hasSavedProps = Object.values(savedProps).some(
            (v) => typeof v === 'number' && Number.isFinite(v),
          );
          const form = mergeMbPropsForm(seed, savedProps);
          const mlBits: string[] = [];
          const mlFillsStartLiq =
            typeof ml?.q_liq_start_td === 'number' &&
            Number.isFinite(ml.q_liq_start_td) &&
            ml.q_liq_start_td > 0 &&
            !hasSavedStartLiquidRate(savedProps);
          if (mlFillsStartLiq) {
            mlBits.push('liq start');
          }
          if (
            typeof ml?.fw_start === 'number' &&
            Number.isFinite(ml.fw_start) &&
            typeof savedProps.fw_start !== 'number'
          ) {
            mlBits.push('WC start');
          }
          let seedNote: string | null = null;
          if (!hasSavedProps && seededKeys.length > 0) {
            seedNote = `prefilled ${seededKeys.length} from reservoirs tab`;
          } else if (hasSavedProps && seededKeys.length > 0) {
            seedNote = 'layer values used where not saved';
          }
          if (mlBits.length > 0) {
            const src = ml?.source ? ` (${ml.source})` : '';
            seedNote = seedNote
              ? `${seedNote}; ML ${mlBits.join(', ')}${src}`
              : `ML ${mlBits.join(', ')}${src}`;
          } else if (ml?.error) {
            seedNote = seedNote
              ? `${seedNote}; ML start unavailable`
              : 'ML start unavailable';
          }
          const propsState: MbPropsLayerState = {
            form,
            saved: { ...form },
            seedNote,
            krTable: Array.isArray(res.config?.kr_table) ? res.config.kr_table : [],
            injWells:
              res.config?.inj_wells && typeof res.config.inj_wells === 'object'
                ? res.config.inj_wells
                : {},
            lastResult: res.last_result ?? null,
            autoPlace: res.config?.auto_place ?? null,
          };
          const persistSeededProps = !hasSavedProps || mlFillsStartLiq;
          return {
            id: layer.scenario_layer_id,
            contour: parseContourVerticesFromConfig(res.config || {}, scenarioCoordinate),
            props: propsState,
            persistSeededProps,
          };
        });

        setContourByLayerId(
          Object.fromEntries(entries.map((e) => [e.id, e.contour])),
        );
        setPropsByLayerId(Object.fromEntries(entries.map((e) => [e.id, e.props])));
        setMapLayerId((prev) =>
          prev && entries.some((e) => e.id === prev) ? prev : entries[0]?.id || '',
        );

        // Fill missing production-well start rates from ML (do not overwrite saved ini_liquid).
        setWellsData((prev) =>
          prev.map((w) => {
            if (w.type_id === injectionWellTypeId) return w;
            if (w.q_start_td != null && w.q_start_td > 0) return w;
            const layerId = w.scenario_layer_id;
            if (!layerId) return w;
            const q = mlById[layerId]?.q_liq_start_td;
            if (typeof q !== 'number' || !Number.isFinite(q) || q <= 0) return w;
            return { ...w, q_start_td: Number(q.toFixed(3)) };
          }),
        );

        // Seeded/ML values live in the form, but simulate reads saved config.
        // Persist them once so analogue q0 / wc0 are not replaced by engine defaults.
        const persistIds = entries.filter((e) => e.persistSeededProps).map((e) => e.id);
        if (persistIds.length) {
          await Promise.all(
            persistIds.map(async (layerId) => {
              const entry = entries.find((e) => e.id === layerId);
              if (!entry) return;
              const existing = await scenariosApi.getMaterialBalance(scenarioId, layerId);
              const props = mbPropsOverridesFromForm(entry.props.form);
              await scenariosApi.putMaterialBalance(scenarioId, layerId, {
                ...existing.config,
                props,
                kvyt:
                  typeof props.kvyt === 'number'
                    ? props.kvyt
                    : existing.config?.kvyt ?? null,
              });
            }),
          );
        }
      } catch (err) {
        if (cancelled) return;
        setContourError(err instanceof Error ? err.message : 'Failed to load contours');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- contourLayersKey + ml binding
  }, [scenarioId, contourLayersKey, scenarioCoordinate, scenarioMlBindingKey, injectionWellTypeId]);

  const selectedContour = mapLayerId ? contourByLayerId[mapLayerId] : undefined;
  const contourVertices = selectedContour?.vertices ?? [];
  const contourSavedVertices = selectedContour?.saved ?? [];
  const contourOrigin = selectedContour?.origin ?? null;

  const contourDirty = useMemo(() => {
    const originDirty =
      (selectedContour?.origin?.lat ?? null) !== (selectedContour?.savedOrigin?.lat ?? null) ||
      (selectedContour?.origin?.lng ?? null) !== (selectedContour?.savedOrigin?.lng ?? null);
    if (originDirty) return true;
    if (contourVertices.length !== contourSavedVertices.length) return true;
    return contourVertices.some(
      (p, i) =>
        p[0] !== contourSavedVertices[i]?.[0] || p[1] !== contourSavedVertices[i]?.[1],
    );
  }, [contourVertices, contourSavedVertices, selectedContour]);

  const mapContours: WellsFieldMapContour[] = useMemo(
    () =>
      scenarioLayers.map((layer, index) => {
        const state = contourByLayerId[layer.scenario_layer_id];
        const isSelected = layer.scenario_layer_id === mapLayerId;
        return {
          id: layer.scenario_layer_id,
          name: layer.layer_name || 'Layer',
          color: contourColorForIndex(index),
          vertices: isSelected ? contourVertices : state?.vertices ?? [],
        };
      }),
    [scenarioLayers, contourByLayerId, mapLayerId, contourVertices],
  );

  const setContourVertices = useCallback(
    (pts: LatLngTuple[]) => {
      if (!mapLayerId) return;
      setContourByLayerId((prev) => {
        const cur = prev[mapLayerId] ?? {
          vertices: [],
          saved: [],
          origin: null,
          savedOrigin: null,
        };
        return {
          ...prev,
          [mapLayerId]: { ...cur, vertices: pts },
        };
      });
    },
    [mapLayerId],
  );

  const handleDatumChange = useCallback(
    (next: MbContourOrigin) => {
      if (!mapLayerId || !isValidGeoOrigin(next)) return;
      const prevOrigin = contourByLayerId[mapLayerId]?.origin ?? null;
      setContourByLayerId((prev) => {
        const cur = prev[mapLayerId] ?? {
          vertices: [],
          saved: [],
          origin: null,
          savedOrigin: null,
        };
        // Keep map vertices (geographic) fixed; only the local-km CRS origin changes.
        return {
          ...prev,
          [mapLayerId]: { ...cur, origin: next },
        };
      });
      if (!isValidGeoOrigin(prevOrigin)) return;
      if (
        Math.abs(prevOrigin.lat - next.lat) < 1e-12 &&
        Math.abs(prevOrigin.lng - next.lng) < 1e-12
      ) {
        return;
      }
      // Preserve geographic well positions: reproject x_km/y_km into the new datum.
      setWellsData((prev) =>
        prev.map((w) => {
          if (w.scenario_layer_id !== mapLayerId) return w;
          if (w.x_km == null || w.y_km == null) return w;
          const xy = reprojectLocalKm(
            { x: Number(w.x_km), y: Number(w.y_km) },
            prevOrigin,
            next,
          );
          return { ...w, x_km: xy.x, y_km: xy.y };
        }),
      );
    },
    [mapLayerId, contourByLayerId],
  );

  const mapWells = useMemo(() => {
    const scenarioPt = parsePointCoordinate(scenarioCoordinate);
    return wellsData
      .filter((w) => w.x_km != null && w.y_km != null)
      .map((w) => {
        const layerOrigin =
          (w.scenario_layer_id && contourByLayerId[w.scenario_layer_id]?.origin) ||
          contourOrigin ||
          resolveContourOrigin({ scenario: scenarioPt });
        const g = localKmToLatLng(Number(w.x_km), Number(w.y_km), layerOrigin);
        return {
          id: w.id,
          name: w.well_name || 'Well',
          lat: g.lat,
          lng: g.lng,
          kind: (w.type_id === injectionWellTypeId ? 'inj' : 'prod') as 'prod' | 'inj',
          contourId: w.scenario_layer_id,
        };
      });
  }, [wellsData, injectionWellTypeId, contourByLayerId, contourOrigin, scenarioCoordinate]);

  /** Bumps when a newer inj edit supersedes an in-flight silent simulate. */
  const injSimGenByLayerRef = useRef<Record<string, number>>({});

  const simulateMbLayer = async (
    layerId: string,
    opts?: { silent?: boolean; gen?: number },
  ): Promise<boolean> => {
    const silent = Boolean(opts?.silent);
    if (!silent) setMbRunningLayerId(layerId);
    setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: null }));
    try {
      if (isMbPropsDirty(layerId)) {
        const saved = await handleSaveMbProps(layerId, { simulate: false });
        if (!saved) return false;
      }
      const out = await scenariosApi.simulateMaterialBalance(scenarioId, {
        scenario_layer_id: layerId,
        persist: true,
      });
      // Drop stale background results if the user already edited injection again.
      if (
        opts?.gen != null &&
        (injSimGenByLayerRef.current[layerId] ?? 0) !== opts.gen
      ) {
        return false;
      }
      setPropsByLayerId((prev) => {
        const cur = prev[layerId];
        if (!cur) return prev;
        return { ...prev, [layerId]: { ...cur, lastResult: out } };
      });
      return true;
    } catch (err) {
      if (
        opts?.gen != null &&
        (injSimGenByLayerRef.current[layerId] ?? 0) !== opts.gen
      ) {
        return false;
      }
      const msg = err instanceof Error ? err.message : 'Material balance simulate failed';
      setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: msg }));
      return false;
    } finally {
      if (!silent) setMbRunningLayerId(null);
    }
  };

  const handleSaveContour = async (opts?: { simulate?: boolean }) => {
    if (!mapLayerId || contourVertices.length < 3) {
      setContourError('Contour needs at least 3 points');
      return;
    }
    if (!isValidGeoOrigin(contourOrigin)) {
      setContourError('Set the datum latitude/longitude before saving the contour');
      return;
    }
    setContourSaving(true);
    setContourError(null);
    try {
      const localOrigin = { lat: contourOrigin.lat, lng: contourOrigin.lng };
      const contour = contourVertices.map(([lat, lng]) =>
        latLngToLocalKm(lat, lng, localOrigin),
      );
      const existing = await scenariosApi.getMaterialBalance(scenarioId, mapLayerId);
      await scenariosApi.putMaterialBalance(scenarioId, mapLayerId, {
        ...existing.config,
        contour,
        contour_origin: localOrigin,
      });
      setContourByLayerId((prev) => ({
        ...prev,
        [mapLayerId]: {
          vertices: contourVertices,
          saved: contourVertices,
          origin: localOrigin,
          savedOrigin: localOrigin,
        },
      }));
      if (opts?.simulate !== false) {
        await simulateMbLayer(mapLayerId);
      }
    } catch (err) {
      setContourError(err instanceof Error ? err.message : 'Failed to save contour');
    } finally {
      setContourSaving(false);
    }
  };

  const handleSelectContour = (id: string) => {
    if (id === mapLayerId) return;
    if (contourDirty) {
      const ok = window.confirm(
        'Current contour has unsaved changes. Switch without saving?',
      );
      if (!ok) return;
      const cur = mapLayerId ? contourByLayerId[mapLayerId] : undefined;
      const fromOrigin = cur?.origin ?? null;
      const toOrigin = cur?.savedOrigin ?? null;
      // revert selected to saved before switching
      setContourByLayerId((prev) => {
        const layer = prev[mapLayerId];
        if (!layer) return prev;
        return {
          ...prev,
          [mapLayerId]: {
            ...layer,
            vertices: layer.saved,
            origin: layer.savedOrigin,
          },
        };
      });
      // Undo in-memory well reprojection if datum was changed
      if (
        mapLayerId &&
        isValidGeoOrigin(fromOrigin) &&
        isValidGeoOrigin(toOrigin) &&
        (fromOrigin.lat !== toOrigin.lat || fromOrigin.lng !== toOrigin.lng)
      ) {
        setWellsData((prev) =>
          prev.map((w) => {
            if (w.scenario_layer_id !== mapLayerId) return w;
            if (w.x_km == null || w.y_km == null) return w;
            const xy = reprojectLocalKm(
              { x: Number(w.x_km), y: Number(w.y_km) },
              fromOrigin,
              toOrigin,
            );
            return { ...w, x_km: xy.x, y_km: xy.y };
          }),
        );
      }
    }
    setMapLayerId(id);
  };

  const wellKindOf = useCallback(
    (w: WellFormData): MbWellRow['kind'] =>
      w.type_id === injectionWellTypeId ? 'inj' : 'prod',
    [injectionWellTypeId],
  );

  const mbRowsForLayer = useCallback(
    (layerId: string): MbWellRow[] =>
      wellsData
        .filter((w) => w.scenario_layer_id === layerId)
        .map((w) => ({
          id: w.id,
          well_name: w.well_name,
          kind: wellKindOf(w),
          t0: w.entry_date?.getFullYear() ?? new Date().getFullYear(),
          t1: w.end_year ?? MB_DEFAULT_END_YEAR,
          x: w.x_km,
          y: w.y_km,
          q_start_td: w.q_start_td,
        })),
    [wellsData, wellKindOf],
  );

  const mbSavedWellsForLayer = useCallback(
    (layerId: string): SeedWellRow[] => {
      const finiteOrNull = (v: number | null | undefined) =>
        typeof v === 'number' && Number.isFinite(v) ? v : null;
      return Object.entries(originalByServerId)
        .filter(([, o]) => o.scenario_layer_id === layerId)
        .map(([serverId, o]) => {
          const kind: MbWellRow['kind'] = o.type_id === injectionWellTypeId ? 'inj' : 'prod';
          return {
            id: serverId,
            well_name: o.well_name,
            kind,
            t0: finiteOrNull(o.entry_date ? Number(o.entry_date.slice(0, 4)) : null),
            t1: finiteOrNull(o.end_year),
            x: finiteOrNull(o.x_km),
            y: finiteOrNull(o.y_km),
            q_start_td:
              kind === 'prod' && o.ini_liquid != null && o.ini_liquid > 0
                ? Number(o.ini_liquid)
                : null,
          };
        });
    },
    [originalByServerId, injectionWellTypeId],
  );

  const isMbWellDirty = useCallback(
    (well: WellFormData): boolean => {
      if (!well.serverId) return true;
      const orig = originalByServerId[well.serverId];
      if (!orig) return true;
      const curEntry = formatEntryDate(well.entry_date) ?? '';
      const origEntry = orig.entry_date ?? '';
      const curQ = well.q_start_td ?? 0;
      const origQ = orig.ini_liquid && orig.ini_liquid > 0 ? orig.ini_liquid : 0;
      return (
        well.well_name.trim() !== orig.well_name ||
        (well.scenario_layer_id ?? '') !== orig.scenario_layer_id ||
        (well.type_id ?? '') !== (orig.type_id ?? '') ||
        curEntry !== origEntry ||
        (well.end_year ?? MB_DEFAULT_END_YEAR) !== (orig.end_year ?? MB_DEFAULT_END_YEAR) ||
        (well.x_km ?? null) !== (orig.x_km ?? null) ||
        (well.y_km ?? null) !== (orig.y_km ?? null) ||
        curQ !== origQ
      );
    },
    [originalByServerId],
  );

  const mbLayerDirty = useCallback(
    (layerId: string) => {
      const layerWells = wellsData.filter((w) => w.scenario_layer_id === layerId);
      if (layerWells.some(isMbWellDirty)) return true;
      // deleted wells that belonged to this layer (best-effort via originals)
      return pendingDeletes.some((id) => originalByServerId[id]?.scenario_layer_id === layerId);
    },
    [wellsData, isMbWellDirty, pendingDeletes, originalByServerId],
  );

  const mbWellsDirty = useMemo(
    () =>
      pendingDeletes.length > 0 ||
      wellsData.some(isMbWellDirty),
    [pendingDeletes, wellsData, isMbWellDirty],
  );

  const handleMbRowsChange = (layerId: string, rows: MbWellRow[]) => {
    const prevLayer = wellsData.filter((w) => w.scenario_layer_id === layerId);
    const prevById = new Map(prevLayer.map((w) => [w.id, w]));
    const removedServerIds = prevLayer
      .filter((w) => w.serverId && !rows.some((r) => r.id === w.id))
      .map((w) => w.serverId!);
    if (removedServerIds.length) {
      setPendingDeletes((d) => Array.from(new Set([...d, ...removedServerIds])));
    }

    const nextLayer: WellFormData[] = rows.map((r) => {
      const prevRow = prevById.get(r.id);
      return {
        id: r.id,
        serverId: prevRow?.serverId ?? null,
        well_name: r.well_name,
        entry_date: entryDateFromYear(r.t0),
        end_year: r.t1 || MB_DEFAULT_END_YEAR,
        scenario_layer_id: layerId,
        type_id: r.kind === 'inj' ? injectionWellTypeId : productionWellTypeId,
        x_km: r.x,
        y_km: r.y,
        q_start_td: r.kind === 'prod' ? r.q_start_td : null,
        isInvalid: false,
      };
    });
    setWellsData((prev) => [
      ...prev.filter((w) => w.scenario_layer_id !== layerId),
      ...nextLayer,
    ]);
  };

  const handleMbPropsChange = (layerId: string, form: MbPropsForm) => {
    setPropsByLayerId((prev) => {
      const cur = prev[layerId];
      if (!cur) {
        return {
          ...prev,
          [layerId]: {
            form,
            saved: form,
            seedNote: null,
            krTable: [],
            injWells: {},
            lastResult: null,
            autoPlace: null,
          },
        };
      }
      return { ...prev, [layerId]: { ...cur, form } };
    });
  };

  const handleDiscardMbProps = (layerId: string) => {
    setPropsByLayerId((prev) => {
      const cur = prev[layerId];
      if (!cur) return prev;
      return { ...prev, [layerId]: { ...cur, form: { ...cur.saved } } };
    });
  };

  const handleDiscardMbWells = (layerId: string) => {
    const layerOriginalIds = Object.entries(originalByServerId)
      .filter(([, o]) => o.scenario_layer_id === layerId)
      .map(([id]) => id);

    setPendingDeletes((d) =>
      d.filter((id) => originalByServerId[id]?.scenario_layer_id !== layerId),
    );

    setWellsData((prev) => {
      const others = prev.filter((w) => w.scenario_layer_id !== layerId);
      const restored: WellFormData[] = layerOriginalIds.map((sid) => {
        const orig = originalByServerId[sid];
        let entryDate: Date | null = null;
        if (orig.entry_date) {
          const [y, m, d] = orig.entry_date.split('-').map(Number);
          entryDate = new Date(y, m - 1, d);
        }
        const qStart =
          orig.ini_liquid != null && orig.ini_liquid > 0 ? Number(orig.ini_liquid) : null;
        return {
          id: sid,
          serverId: sid,
          well_name: orig.well_name,
          entry_date: entryDate,
          end_year: orig.end_year ?? MB_DEFAULT_END_YEAR,
          scenario_layer_id: layerId,
          type_id: orig.type_id ?? productionWellTypeId,
          x_km: orig.x_km ?? null,
          y_km: orig.y_km ?? null,
          q_start_td: qStart,
          isInvalid: false,
        };
      });
      return [...others, ...restored];
    });

    setSelectedWellId((cur) => {
      if (!cur) return cur;
      if (layerOriginalIds.includes(cur)) return cur;
      return null;
    });
  };

  const isMbPropsDirty = useCallback(
    (layerId: string) => {
      const state = propsByLayerId[layerId];
      if (!state) return false;
      return !mbPropsFormsEqual(state.form, state.saved);
    },
    [propsByLayerId],
  );

  const mbPropsDirty = useMemo(
    () => scenarioLayers.some((l) => isMbPropsDirty(l.scenario_layer_id)),
    [scenarioLayers, isMbPropsDirty],
  );

  const handleSaveMbProps = async (
    layerId: string,
    opts?: { simulate?: boolean },
  ): Promise<boolean> => {
    const state = propsByLayerId[layerId];
    if (!state) return true;
    setMbPropsSaving(true);
    setContourError(null);
    try {
      const existing = await scenariosApi.getMaterialBalance(scenarioId, layerId);
      const props = mbPropsOverridesFromForm(state.form);
      const nextConfig = {
        ...existing.config,
        props,
        kr_table: state.krTable || [],
        relperm_mode: (state.krTable?.length ?? 0) >= 2 ? 'table' : 'corey',
        // Keep top-level kvyt aligned when user edits Ed
        kvyt:
          typeof props.kvyt === 'number'
            ? props.kvyt
            : existing.config?.kvyt ?? null,
      };
      await scenariosApi.putMaterialBalance(scenarioId, layerId, nextConfig);
      setPropsByLayerId((prev) => {
        const cur = prev[layerId];
        if (!cur) return prev;
        return {
          ...prev,
          [layerId]: { ...cur, saved: { ...cur.form }, seedNote: null },
        };
      });
      if (opts?.simulate !== false) {
        await simulateMbLayer(layerId);
      }
      return true;
    } catch (err) {
      setContourError(err instanceof Error ? err.message : 'Failed to save properties');
      return false;
    } finally {
      setMbPropsSaving(false);
    }
  };

  const handleSaveKrTable = async (
    layerId: string,
    table: number[][],
    propsPatch?: { so?: number; kvyt?: number },
  ): Promise<void> => {
    setMbPropsSaving(true);
    setContourError(null);
    try {
      const existing = await scenariosApi.getMaterialBalance(scenarioId, layerId);
      const state = propsByLayerId[layerId];
      const baseForm = state?.form ?? emptyMbPropsForm();
      const nextForm =
        propsPatch && (propsPatch.so != null || propsPatch.kvyt != null)
          ? {
              ...baseForm,
              ...(typeof propsPatch.so === 'number' ? { so: propsPatch.so } : {}),
              ...(typeof propsPatch.kvyt === 'number' ? { kvyt: propsPatch.kvyt } : {}),
            }
          : baseForm;
      const props = mbPropsOverridesFromForm(nextForm);
      await scenariosApi.putMaterialBalance(scenarioId, layerId, {
        ...existing.config,
        props: { ...existing.config?.props, ...props },
        // Keep top-level kvyt aligned when E_d is patched from relperm
        kvyt:
          typeof nextForm.kvyt === 'number'
            ? nextForm.kvyt
            : existing.config?.kvyt ?? null,
        kr_table: table,
        relperm_mode: table.length >= 2 ? 'table' : 'corey',
      });
      setPropsByLayerId((prev) => {
        const cur = prev[layerId];
        if (!cur) {
          return {
            ...prev,
            [layerId]: {
              form: nextForm,
              saved: { ...nextForm },
              seedNote: null,
              krTable: table,
              injWells: {},
              lastResult: null,
              autoPlace: null,
            },
          };
        }
        return {
          ...prev,
          [layerId]: {
            ...cur,
            form: nextForm,
            saved: { ...nextForm },
            krTable: table,
          },
        };
      });
      await simulateMbLayer(layerId);
    } catch (err) {
      setContourError(err instanceof Error ? err.message : 'Failed to save kr table');
      throw err;
    } finally {
      setMbPropsSaving(false);
    }
  };

  const handleSaveMbWells = async (opts?: {
    layerId?: string;
    simulate?: boolean;
  }): Promise<boolean> => {
    if (!productionWellTypeId) {
      setError('Production well type not found');
      return false;
    }
    if (wellsData.some((w) => !w.well_name.trim())) {
      setError('Every well needs an id (name)');
      return false;
    }
    if (wellsData.some((w) => !w.entry_date)) {
      setError('Every well needs a start year');
      return false;
    }
    if (wellsData.some((w) => w.x_km == null || w.y_km == null)) {
      setError('Every well needs x and y (km) for material balance');
      return false;
    }
    if (wellsData.some((w) => wellKindOf(w) === 'inj') && !injectionWellTypeId) {
      setError('Injection well type not found');
      return false;
    }

    setMbWellsSaving(true);
    try {
      const ok = await handleSaveWells();
      if (!ok) return false;
      if (opts?.simulate !== false) {
        const ids = opts?.layerId
          ? [opts.layerId]
          : scenarioLayers.map((l) => l.scenario_layer_id);
        for (const id of ids) {
          await simulateMbLayer(id);
        }
      }
      return true;
    } finally {
      setMbWellsSaving(false);
    }
  };

  const handleRunMb = async (layerId: string) => {
    try {
      if (isMbPropsDirty(layerId)) {
        const ok = await handleSaveMbProps(layerId, { simulate: false });
        if (!ok) throw new Error('Failed to save properties before simulate');
      }
      if (mbLayerDirty(layerId)) {
        const ok = await handleSaveMbWells({ layerId, simulate: false });
        if (!ok) throw new Error('Failed to save wells before simulate');
      }
      await simulateMbLayer(layerId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Material balance simulate failed';
      setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: msg }));
    }
  };

  const handleInjWellsChange = (
    layerId: string,
    next: Record<string, Record<string, number>>,
  ) => {
    setPropsByLayerId((prev) => {
      const cur = prev[layerId];
      if (!cur) return prev;
      return { ...prev, [layerId]: { ...cur, injWells: next } };
    });
  };

  const handleOptimizeAccept = async (layerId: string, proposal: MbOptimizeProposal) => {
    if (proposal.mode === 'schedule_and_injection') {
      handleMbRowsChange(layerId, proposal.wells);
    }
    setPropsByLayerId((prev) => {
      const cur = prev[layerId];
      if (!cur) return prev;
      return {
        ...prev,
        [layerId]: {
          ...cur,
          injWells: proposal.injWells,
          lastResult: proposal.result,
        },
      };
    });
    // Persist injection overrides; keep proposed result visible (no immediate re-simulate).
    try {
      const existing = await scenariosApi.getMaterialBalance(scenarioId, layerId);
      await scenariosApi.putMaterialBalance(scenarioId, layerId, {
        ...existing.config,
        inj_wells: proposal.injWells,
      });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to persist optimized injection';
      setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: msg }));
    }
  };

  const handleInjWellsCommit = async (
    layerId: string,
    next: Record<string, Record<string, number>>,
  ) => {
    // Keep UI editable: persist + silent background simulate; ignore stale gens.
    const gen = (injSimGenByLayerRef.current[layerId] ?? 0) + 1;
    injSimGenByLayerRef.current[layerId] = gen;
    setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: null }));
    setPropsByLayerId((prev) => {
      const cur = prev[layerId];
      if (!cur) return prev;
      return { ...prev, [layerId]: { ...cur, injWells: next } };
    });
    try {
      const existing = await scenariosApi.getMaterialBalance(scenarioId, layerId);
      if ((injSimGenByLayerRef.current[layerId] ?? 0) !== gen) return;
      await scenariosApi.putMaterialBalance(scenarioId, layerId, {
        ...existing.config,
        inj_wells: next,
      });
      if ((injSimGenByLayerRef.current[layerId] ?? 0) !== gen) return;
      // Do not flip saving/running — recalculation must not interrupt injection edits.
      void simulateMbLayer(layerId, { silent: true, gen });
    } catch (err) {
      if ((injSimGenByLayerRef.current[layerId] ?? 0) !== gen) return;
      const msg =
        err instanceof Error ? err.message : 'Failed to update injection / re-simulate';
      setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: msg }));
    }
  };

  const handleAutoPlaceApply = async (layerId: string, result: AutoPlaceResult) => {
    if (!productionWellTypeId) {
      setError('Production well type not found');
      return;
    }
    if (result.wells.some((w) => w.kind === 'inj') && !injectionWellTypeId) {
      setError('Injection well type not found');
      return;
    }

    setMbWellsSaving(true);
    setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: null }));
    setError(null);
    try {
      const applyMode = result.applyMode === 'delta' ? 'delta' : 'replace';
      const prevLayer = wellsData.filter((w) => w.scenario_layer_id === layerId);
      const prevByName = new Map(
        prevLayer.map((w) => [w.well_name.trim().toLowerCase(), w] as const),
      );

      const items_to_create: Record<string, ScenarioWellLayerCreateBatch> = {};
      const layerBatch = ensureLayerCreateBatch(items_to_create, layerId);
      const items_to_update: ScenarioWellBulkUpdateItem[] = [];
      let deletes: string[] = [];

      const nextLocal: WellFormData[] = [];

      if (applyMode === 'replace') {
        // Full rewrite: delete all existing, create the scheduled set.
        deletes = prevLayer
          .map((w) => w.serverId)
          .filter((id): id is string => Boolean(id));

        result.wells.forEach((w, i) => {
          const well: WellFormData = {
            id: `auto-${Date.now()}-${i}`,
            serverId: null,
            well_name: w.well_name,
            entry_date: entryDateFromYear(w.t0),
            end_year: w.t1,
            scenario_layer_id: layerId,
            type_id: w.kind === 'inj' ? injectionWellTypeId : productionWellTypeId,
            x_km: w.x,
            y_km: w.y,
            q_start_td: w.kind === 'prod' ? w.q_start_td : null,
            isInvalid: false,
          };
          nextLocal.push(well);
          const entryDate = formatEntryDate(well.entry_date);
          if (!entryDate) return;
          const item = {
            entry_date: entryDate,
            well_name: well.well_name.trim(),
            x_km: well.x_km,
            y_km: well.y_km,
            ini_liquid: well.q_start_td ?? 0,
            end_year: well.end_year,
          };
          if (well.type_id === injectionWellTypeId) layerBatch.injection.push(item);
          else layerBatch.production.push(item);
        });
      } else {
        // Delta: keep untouched wells; create / update / delete only the difference.
        const nextNames = new Set(
          result.wells.map((w) => w.well_name.trim().toLowerCase()).filter(Boolean),
        );
        deletes = prevLayer
          .filter((w) => !nextNames.has(w.well_name.trim().toLowerCase()))
          .map((w) => w.serverId)
          .filter((id): id is string => Boolean(id));

        result.wells.forEach((w, i) => {
          const key = w.well_name.trim().toLowerCase();
          const prev = prevByName.get(key);
          const typeId = w.kind === 'inj' ? injectionWellTypeId : productionWellTypeId;
          const entryDate = entryDateFromYear(w.t0);
          const entryDateStr = formatEntryDate(entryDate);

          if (prev?.serverId) {
            nextLocal.push({
              ...prev,
              well_name: w.well_name,
              entry_date: entryDate,
              end_year: w.t1,
              type_id: typeId,
              x_km: w.x,
              y_km: w.y,
              q_start_td: w.kind === 'prod' ? w.q_start_td : null,
              isInvalid: false,
            });
            items_to_update.push({
              id: prev.serverId,
              well_name: w.well_name.trim(),
              scenario_layer_id: layerId,
              type_id: typeId ?? undefined,
              entry_date: entryDateStr,
              x_km: w.x,
              y_km: w.y,
              ini_liquid: w.kind === 'prod' ? w.q_start_td ?? 0 : 0,
              end_year: w.t1,
            });
          } else {
            const well: WellFormData = {
              id: `auto-${Date.now()}-${i}`,
              serverId: null,
              well_name: w.well_name,
              entry_date: entryDate,
              end_year: w.t1,
              scenario_layer_id: layerId,
              type_id: typeId,
              x_km: w.x,
              y_km: w.y,
              q_start_td: w.kind === 'prod' ? w.q_start_td : null,
              isInvalid: false,
            };
            nextLocal.push(well);
            if (!entryDateStr) return;
            const item = {
              entry_date: entryDateStr,
              well_name: well.well_name.trim(),
              x_km: well.x_km,
              y_km: well.y_km,
              ini_liquid: well.q_start_td ?? 0,
              end_year: well.end_year,
            };
            if (well.type_id === injectionWellTypeId) layerBatch.injection.push(item);
            else layerBatch.production.push(item);
          }
        });
      }

      setWellsData((prev) => [
        ...prev.filter((w) => w.scenario_layer_id !== layerId),
        ...nextLocal,
      ]);

      const scenarioWells = await scenarioWellsApi.patchScenarioWellsBulk(scenarioId, {
        items_to_create,
        items_to_update,
        items_to_delete: deletes,
      });

      const existing = await scenariosApi.getMaterialBalance(scenarioId, layerId);
      const autoPlaceConfig: MbAutoPlaceConfig | null =
        result.source === 'manual'
          ? (existing.config?.auto_place ?? null)
          : {
              pattern: result.meta.pattern,
              spacingM: result.meta.spacingM,
              production: result.meta.production,
              injection: result.meta.injection,
              injectorsOn: result.meta.nInj > 0,
              startYear: result.meta.startYear,
              maxProdPerYear: result.meta.maxProdPerYear,
              maxInjPerYear: result.meta.maxInjPerYear,
            };

      await scenariosApi.putMaterialBalance(scenarioId, layerId, {
        ...existing.config,
        inj_field: result.injField,
        inj_wells: {},
        auto_place: autoPlaceConfig,
      });

      setPropsByLayerId((prev) => {
        const cur = prev[layerId];
        if (!cur) return prev;
        return {
          ...prev,
          [layerId]: { ...cur, injWells: {}, autoPlace: autoPlaceConfig },
        };
      });

      setPendingDeletes((d) => d.filter((id) => !deletes.includes(id)));
      applyServerWellsState(
        scenarioWells,
        scenarioLayers,
        productionWellTypeId,
        injectionWellTypeId,
      );

      await simulateMbLayer(layerId);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to auto-place wells';
      setError(msg);
      setMbRunErrorByLayerId((prev) => ({ ...prev, [layerId]: msg }));
      throw err;
    } finally {
      setMbWellsSaving(false);
    }
  };

  useEffect(() => {
    if (!scenarioLayers.length) return;
    setInteractivePlanByLayerId((prev) => {
      const next = { ...prev };
      let changed = false;
      scenarioLayers.forEach((layer) => {
        if (!next[layer.scenario_layer_id]) {
          next[layer.scenario_layer_id] = createDefaultInteractiveLayerPlan();
          changed = true;
        } else if (next[layer.scenario_layer_id].injectionWellsPerYear.length !== next[layer.scenario_layer_id].wellsPerYear.length) {
          const len = next[layer.scenario_layer_id].wellsPerYear.length;
          const inj = next[layer.scenario_layer_id].injectionWellsPerYear;
          next[layer.scenario_layer_id] = {
            ...next[layer.scenario_layer_id],
            injectionWellsPerYear:
              inj.length === len
                ? inj
                : inj.length > len
                  ? inj.slice(0, len)
                  : [...inj, ...Array(len - inj.length).fill(0)],
          };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [scenarioLayers]);


  useEffect(() => {
    if (wellsInnerTab !== 'interactive' || !scenarioId) {
      return;
    }
    if (scenarioLayers.length === 0) {
      setLoadingBaseCurves(false);
      return;
    }
    if (baseCurvesCache?.key === layersCacheKey) {
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingBaseCurves(true);
      setBaseCurvesProgress('Loading base well curves...');
      try {
        if (cancelled) return;
        const curves: Record<string, BaseWellCurvesResponse> = {};
        const errors: Record<string, string> = {};
        const settled = await Promise.allSettled(
          scenarioLayers.map((layer) =>
            scenarioLayersApi.getBaseWellCurves(layer.scenario_layer_id, (progress: BaseWellCurvesProgress) => {
              if (!cancelled) {
                setBaseCurvesProgress(`${layer.layer_name}: ${progress.message}`);
              }
            }),
          ),
        );
        if (cancelled) return;
        settled.forEach((r, i) => {
          const id = scenarioLayers[i].scenario_layer_id;
          if (r.status === 'fulfilled') {
            curves[id] = r.value;
          } else {
            errors[id] = apiErrorDetail(r.reason);
          }
        });
        setBaseCurvesCache({ key: layersCacheKey, curves, errors });
      } finally {
        if (!cancelled) {
          setLoadingBaseCurves(false);
          setBaseCurvesProgress(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      setLoadingBaseCurves(false);
    };
  }, [wellsInnerTab, scenarioId, scenarioLayers, layersCacheKey, baseCurvesCache?.key]);

  const predictedQStartTd = useCallback(
    (layerId: string | null | undefined): number | null => {
      if (!layerId) return null;
      const q = mlStartByLayerId[layerId]?.q_liq_start_td;
      if (typeof q !== 'number' || !Number.isFinite(q) || q <= 0) return null;
      return Number(q.toFixed(3));
    },
    [mlStartByLayerId],
  );

  const handleAddWell = () => {
    if (scenarioLayers.length === 0) {
      setError('Please create at least one reservoir before adding wells.');
      return;
    }
    const layerId = scenarioLayers[0].scenario_layer_id;
    const newWell: WellFormData = {
      id: `temp-${Date.now()}-${Math.random()}`,
      serverId: null,
      well_name: '',
      entry_date: null,
      end_year: MB_DEFAULT_END_YEAR,
      scenario_layer_id: layerId,
      type_id: productionWellTypeId,
      x_km: null,
      y_km: null,
      q_start_td: predictedQStartTd(layerId),
      isInvalid: false,
    };
    setWellsData(prev => [...prev, newWell]);
  };

  const handleLayerChange = (id: string, layerId: string) => {
    setWellsData(prev =>
      prev.map((well) => {
        if (well.id !== id) return well;
        const isInj = well.type_id === injectionWellTypeId;
        return {
          ...well,
          scenario_layer_id: layerId,
          isInvalid: false,
          q_start_td: isInj ? null : predictedQStartTd(layerId) ?? well.q_start_td,
        };
      }),
    );
  };

  const handleDeleteWell = (id: string) => {
    setWellsData(prev => {
      const well = prev.find(w => w.id === id);
      if (well?.serverId) {
        setPendingDeletes(p => p.includes(well.serverId!) ? p : [...p, well.serverId!]);
      }
      return prev.filter(w => w.id !== id);
    });
  };

  const handleWellNameChange = (id: string, name: string) => {
    setWellsData(prev =>
      prev.map(well =>
        well.id === id ? { ...well, well_name: name } : well
      )
    );
  };

  const handleEntryDateChange = (id: string, date: Date | null) => {
    setWellsData(prev =>
      prev.map(well =>
        well.id === id ? { ...well, entry_date: date } : well
      )
    );
  };

  const handleWellTypeChange = (id: string, typeId: string) => {
    setWellsData((prev) =>
      prev.map((well) => {
        if (well.id !== id) return well;
        const isInj = typeId === injectionWellTypeId;
        return {
          ...well,
          type_id: typeId,
          q_start_td: isInj
            ? null
            : well.q_start_td != null && well.q_start_td > 0
              ? well.q_start_td
              : predictedQStartTd(well.scenario_layer_id),
        };
      }),
    );
  };

  const handleImportWellsRows = (rows: ImportedScenarioWellRow[]) => {
    const existingServerIds = wellsData
      .map((well) => well.serverId)
      .filter((value): value is string => !!value);

    const importedWells: WellFormData[] = rows.map((row, index) => {
      const typeId = row.type_id ?? productionWellTypeId;
      const isInj = typeId === injectionWellTypeId;
      return {
        id: `imported-${Date.now()}-${index}-${Math.random()}`,
        serverId: null,
        well_name: row.well_name,
        entry_date: row.entry_date,
        end_year: MB_DEFAULT_END_YEAR,
        scenario_layer_id: row.scenario_layer_id,
        type_id: typeId,
        x_km: null,
        y_km: null,
        q_start_td: isInj ? null : predictedQStartTd(row.scenario_layer_id),
        isInvalid: !scenarioLayers.some((layer) => layer.scenario_layer_id === row.scenario_layer_id),
      };
    });

    setWellsData(importedWells);
    setPendingDeletes((prev) => Array.from(new Set([...prev, ...existingServerIds])));
    setError(null);
  };

  const handleInteractivePlanChange = (layerId: string, plan: InteractiveLayerPlan) => {
    const injectionWellsPerYear =
      plan.injectionWellsPerYear?.length === plan.wellsPerYear.length
        ? plan.injectionWellsPerYear
        : Array(plan.wellsPerYear.length).fill(0);
    setInteractivePlanByLayerId((prev) => ({
      ...prev,
      [layerId]: {
        ...plan,
        injectionWellsPerYear,
        injectionRatio: normalizeWellTypeRatio(plan.injectionRatio ?? DEFAULT_WELL_TYPE_RATIO),
        injectionRatioLinked: plan.injectionRatioLinked ?? false,
      },
    }));
    setInteractiveDirty(true);
  };

  const handleSaveInteractiveWells = async (): Promise<boolean> => {
    if (!productionWellTypeId) {
      setError('Production well type not found');
      return false;
    }
    if (scenarioLayers.length === 0) {
      setError('No layers found in scenario. Please create a layer first.');
      return false;
    }

    const items_to_create: Record<string, ScenarioWellLayerCreateBatch> = {};

    scenarioLayers.forEach((layer) => {
      const layerPlan = interactivePlanByLayerId[layer.scenario_layer_id];
      if (!layerPlan) return;
      let productionWellNumber = 1;
      let injectionWellNumber = 1;
      const prefix = normalizeLayerPrefix(layer.layer_name || layer.scenario_layer_id);
      const yearSpan = layerPlan.wellsPerYear.length;
      const layerBatch = ensureLayerCreateBatch(items_to_create, layer.scenario_layer_id);

      for (let offset = 0; offset < yearSpan; offset += 1) {
        const productionCount = Math.max(0, Math.floor(layerPlan.wellsPerYear[offset] || 0));
        const injectionCount = Math.max(0, Math.floor(layerPlan.injectionWellsPerYear[offset] || 0));
        if (productionCount === 0 && injectionCount === 0) {
          continue;
        }

        const year = layerPlan.startYear + offset;
        const schedule = buildYearWellEntrySchedule(year, productionCount, injectionCount);

        schedule.forEach(({ kind, entryDate }) => {
          if (kind === 'production') {
            layerBatch.production.push([entryDate, `${prefix}_${productionWellNumber}`]);
            productionWellNumber += 1;
            return;
          }

          if (!injectionWellTypeId) {
            return;
          }
          layerBatch.injection.push([entryDate, `${prefix}_inj_${injectionWellNumber}`]);
          injectionWellNumber += 1;
        });
      }

      if (layerBatch.production.length === 0 && layerBatch.injection.length === 0) {
        delete items_to_create[layer.scenario_layer_id];
      }
    });

    const plannedInjectionWells = scenarioLayers.reduce((acc, layer) => {
      const layerPlan = interactivePlanByLayerId[layer.scenario_layer_id];
      if (!layerPlan) return acc;
      return acc + layerPlan.injectionWellsPerYear.reduce((sum, countRaw) => sum + Math.max(0, Math.floor(countRaw || 0)), 0);
    }, 0);
    if (plannedInjectionWells > 0 && !injectionWellTypeId) {
      setError('Injection well type not found');
      return false;
    }

    const items_to_delete = savedScenarioWells
      .map((w) => w.scenario_well_id || w.id)
      .filter((id): id is string => !!id);

    if (countGroupedWellsCreate(items_to_create) === 0 && items_to_delete.length > 0) {
      setError(
        'The interactive plan has no wells (all bars are zero). Set the drilling schedule on the bars before saving.',
      );
      return false;
    }

    try {
      setSaving(true);
      setError(null);
      const scenarioWells = await scenarioWellsApi.patchScenarioWellsBulk(scenarioId, {
        items_to_create,
        items_to_update: [],
        items_to_delete,
      });
      const updatedLayers = await scenarioLayersApi.getScenarioLayers(scenarioId);
      setScenarioLayers(updatedLayers);
      applyServerWellsState(
        scenarioWells,
        updatedLayers,
        productionWellTypeId,
        injectionWellTypeId,
      );
      return true;
    } catch (err: any) {
      console.error('Failed to save wells from interactive plan:', err);
      setError(err?.response?.data?.detail || 'Failed to save wells');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWells = async (): Promise<boolean> => {
    if (SHOW_LEGACY_WELLS_UI && wellsInnerTab === 'interactive') {
      return handleSaveInteractiveWells();
    }

    if (!productionWellTypeId) {
      setError('Production well type not found');
      return false;
    }

    if (scenarioLayers.length === 0) {
      setError('No layers found in scenario. Please create a layer first.');
      return false;
    }

    const invalidWells = wellsData.filter(
      w => !w.well_name.trim() || !w.entry_date || !w.scenario_layer_id || !w.type_id
    );
    if (invalidWells.length > 0) {
      setError('Please fill in all fields for all wells, including reservoir and well type');
      return false;
    }

    const injectionWellsInForm = wellsData.filter((w) => w.type_id === injectionWellTypeId);
    if (injectionWellsInForm.length > 0 && !injectionWellTypeId) {
      setError('Injection well type not found');
      return;
    }

    const wellsWithInvalidLayers = wellsData.filter(w => {
      if (!w.scenario_layer_id) return true;
      return !scenarioLayers.some(l => l.scenario_layer_id === w.scenario_layer_id);
    });
    if (wellsWithInvalidLayers.length > 0) {
      setError('Some wells are bound to non-existent reservoirs. Please fix this before saving.');
      return;
    }

    function isWellDirty(well: WellFormData): boolean {
      if (!well.serverId) return false;
      const orig = originalByServerId[well.serverId];
      if (!orig) return true;
      const curEntry = formatEntryDate(well.entry_date) ?? '';
      const origEntry = orig.entry_date ?? '';
      const curQ = well.q_start_td ?? 0;
      const origQ = orig.ini_liquid ?? 0;
      return (
        well.well_name.trim() !== orig.well_name ||
        (well.scenario_layer_id ?? '') !== orig.scenario_layer_id ||
        (well.type_id ?? '') !== (orig.type_id ?? '') ||
        curEntry !== origEntry ||
        (well.end_year ?? MB_DEFAULT_END_YEAR) !== (orig.end_year ?? MB_DEFAULT_END_YEAR) ||
        (well.x_km ?? null) !== (orig.x_km ?? null) ||
        (well.y_km ?? null) !== (orig.y_km ?? null) ||
        curQ !== origQ
      );
    }

    const items_to_create: Record<string, ScenarioWellLayerCreateBatch> = {};
    wellsData
      .filter((well) => !well.serverId)
      .forEach((well) => {
        const layerId = well.scenario_layer_id!;
        const entryDate = formatEntryDate(well.entry_date);
        if (!entryDate) {
          return;
        }
        const layerBatch = ensureLayerCreateBatch(items_to_create, layerId);
        const item = {
          entry_date: entryDate,
          well_name: well.well_name.trim(),
          x_km: well.x_km,
          y_km: well.y_km,
          ini_liquid: well.q_start_td ?? 0,
          end_year: well.end_year ?? MB_DEFAULT_END_YEAR,
        };
        if (well.type_id === injectionWellTypeId) {
          layerBatch.injection.push(item);
        } else {
          layerBatch.production.push(item);
        }
      });

    // Only wells that exist on server and were changed (dirty); deleted ones are only in pendingDeletes
    const items_to_update: ScenarioWellBulkUpdateItem[] = wellsData
      .filter((w): w is WellFormData & { serverId: string } => w.serverId != null && isWellDirty(w))
      .map((w) => ({
        id: w.serverId,
        well_name: w.well_name.trim(),
        scenario_layer_id: w.scenario_layer_id ?? undefined,
        type_id: w.type_id ?? undefined,
        entry_date: formatEntryDate(w.entry_date),
        x_km: w.x_km,
        y_km: w.y_km,
        ini_liquid: w.q_start_td ?? 0,
        end_year: w.end_year ?? MB_DEFAULT_END_YEAR,
      }));

    try {
      setSaving(true);
      setError(null);

      const scenarioWells = await scenarioWellsApi.patchScenarioWellsBulk(scenarioId, {
        items_to_create,
        items_to_update,
        items_to_delete: pendingDeletes,
      });

      const updatedLayers = await scenarioLayersApi.getScenarioLayers(scenarioId);
      setScenarioLayers(updatedLayers);
      applyServerWellsState(
        scenarioWells,
        updatedLayers,
        productionWellTypeId,
        injectionWellTypeId,
      );
      return true;
    } catch (err: any) {
      console.error('Failed to save wells:', err);
      setError(err?.response?.data?.detail || 'Failed to save wells');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const pendingSaveRef = React.useRef<() => Promise<boolean>>(async () => true);
  pendingSaveRef.current = async () => {
    if (!SHOW_LEGACY_WELLS_UI) {
      const layersToSim = new Set<string>();
      if (contourDirty && contourVertices.length >= 3 && mapLayerId) {
        await handleSaveContour({ simulate: false });
        layersToSim.add(mapLayerId);
      }
      if (mbPropsDirty) {
        for (const layer of scenarioLayers) {
          if (isMbPropsDirty(layer.scenario_layer_id)) {
            const ok = await handleSaveMbProps(layer.scenario_layer_id, {
              simulate: false,
            });
            if (!ok) return false;
            layersToSim.add(layer.scenario_layer_id);
          }
        }
      }
      if (mbWellsDirty) {
        const ok = await handleSaveMbWells({ simulate: false });
        if (!ok) return false;
        for (const layer of scenarioLayers) {
          layersToSim.add(layer.scenario_layer_id);
        }
      }
      for (const layerId of layersToSim) {
        await simulateMbLayer(layerId);
      }
      return true;
    }
    if (wellsInnerTab === 'interactive') {
      if (!interactiveDirty) return true;
      return handleSaveInteractiveWells();
    }
    const hasNew = wellsData.some((w) => !w.serverId);
    const hasDeletes = pendingDeletes.length > 0;
    const hasUpdates = wellsData.some((w) => {
      if (!w.serverId) return false;
      const orig = originalByServerId[w.serverId];
      if (!orig) return true;
      const entry =
        w.entry_date instanceof Date
          ? `${w.entry_date.getFullYear()}-${String(w.entry_date.getMonth() + 1).padStart(2, '0')}-${String(w.entry_date.getDate()).padStart(2, '0')}`
          : undefined;
      return (
        (w.well_name || '') !== (orig.well_name || '')
        || (w.scenario_layer_id || '') !== (orig.scenario_layer_id || '')
        || (w.type_id || '') !== (orig.type_id || '')
        || entry !== orig.entry_date
      );
    });
    if (!hasNew && !hasDeletes && !hasUpdates) return true;
    return handleSaveWells();
  };

  useEffect(() => {
    if (!onRegisterPendingSave) return undefined;
    onRegisterPendingSave(() => pendingSaveRef.current());
    return () => onRegisterPendingSave(null);
  }, [onRegisterPendingSave]);

  const handleSaveClick = async () => {
    if (wellsInnerTab === 'interactive') {
      setInteractiveSaveConfirmOpen(true);
      return;
    }
    await handleSaveWells();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Scenario Wells
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Field layout for material balance: contour first, then injection and well schedule.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {contourError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setContourError(null)}>
          {contourError}
        </Alert>
      )}

      {scenarioLayers.length === 0 && !loadingData ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please create at least one reservoir before drawing the field map.
        </Alert>
      ) : (
        <WellsFieldMap
          contours={mapContours}
          selectedId={mapLayerId}
          onSelectId={handleSelectContour}
          vertices={contourVertices}
          onVerticesChange={setContourVertices}
          datum={contourOrigin}
          onDatumChange={handleDatumChange}
          wells={mapWells}
          flyToCoordinate={scenarioCoordinate}
          saving={contourSaving}
          dirty={contourDirty}
          onSaveContour={() => void handleSaveContour()}
        />
      )}

      {scenarioLayers.length > 0 &&
        scenarioLayers.map((layer, index) => {
          const propsState = propsByLayerId[layer.scenario_layer_id];
          const propsForm = propsState?.form ?? emptyMbPropsForm();
          const contourState = contourByLayerId[layer.scenario_layer_id];
          const scenarioPt = parsePointCoordinate(scenarioCoordinate);
          const contourOriginLocal = isValidGeoOrigin(contourState?.origin)
            ? contourState.origin
            : resolveContourOrigin({
                saved: contourState?.origin ?? null,
                scenario: scenarioPt,
              });
          const contourKm =
            contourState?.vertices?.length
              ? contourState.vertices.map(([lat, lng]) =>
                  latLngToLocalKm(lat, lng, contourOriginLocal),
                )
              : [];
          return (
            <MaterialBalanceWellsSection
              key={layer.scenario_layer_id}
              scenarioId={scenarioId}
              layerId={layer.scenario_layer_id}
              layerName={layer.layer_name || 'Layer'}
              layerColor={contourColorForIndex(index)}
              wells={mbRowsForLayer(layer.scenario_layer_id)}
              selectedWellId={selectedWellId}
              onSelectWell={setSelectedWellId}
              onChange={(rows) => handleMbRowsChange(layer.scenario_layer_id, rows)}
              dirty={mbLayerDirty(layer.scenario_layer_id)}
              saving={mbWellsSaving || mbPropsSaving || mbRunningLayerId === layer.scenario_layer_id}
              onSave={() => void handleSaveMbWells({ layerId: layer.scenario_layer_id })}
              onDiscard={() => handleDiscardMbWells(layer.scenario_layer_id)}
              propsForm={propsForm}
              onPropsChange={(form) => handleMbPropsChange(layer.scenario_layer_id, form)}
              propsDirty={isMbPropsDirty(layer.scenario_layer_id)}
              propsSeedNote={propsState?.seedNote ?? null}
              onSaveProps={() => void handleSaveMbProps(layer.scenario_layer_id)}
              onDiscardProps={() => handleDiscardMbProps(layer.scenario_layer_id)}
              krTable={propsState?.krTable ?? null}
              onSaveKrTable={(table) => handleSaveKrTable(layer.scenario_layer_id, table)}
              mbResult={propsState?.lastResult ?? null}
              contourKm={contourKm}
              contourOrigin={contourOriginLocal}
              autoPlaceConfig={propsState?.autoPlace ?? null}
              savedWells={mbSavedWellsForLayer(layer.scenario_layer_id)}
              year0={new Date().getFullYear()}
              year1={new Date().getFullYear() + 16}
              onAutoPlaceApply={(result) =>
                handleAutoPlaceApply(layer.scenario_layer_id, result)
              }
              autoPlaceApplying={mbWellsSaving}
              injWells={propsState?.injWells ?? {}}
              onInjWellsChange={(next) =>
                handleInjWellsChange(layer.scenario_layer_id, next)
              }
              onInjWellsCommit={(next) =>
                void handleInjWellsCommit(layer.scenario_layer_id, next)
              }
              onOptimizeAccept={(proposal) =>
                void handleOptimizeAccept(layer.scenario_layer_id, proposal)
              }
              onRunMb={() => void handleRunMb(layer.scenario_layer_id)}
              mbRunning={mbRunningLayerId === layer.scenario_layer_id}
              mbRunError={mbRunErrorByLayerId[layer.scenario_layer_id] ?? null}
            />
          );
        })}

      {SHOW_LEGACY_WELLS_UI && scenarioLayers.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={wellsInnerTab}
            onChange={(_, v: WellsInnerTab) => setWellsInnerTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Interactive" value="interactive" />
            <Tab label="Table input" value="manual" />
          </Tabs>

          {wellsInnerTab === 'manual' && (
            <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Wells
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <TextField
                size="small"
                label="Filter by name"
                placeholder="Name"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Filter by date"
                  value={filterDate}
                  onChange={setFilterDate}
                  slotProps={{
                    textField: { size: 'small', sx: { minWidth: 160 } },
                  }}
                />
              </LocalizationProvider>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Filter by reservoir</InputLabel>
                <Select
                  label="Filter by reservoir"
                  value={filterLayerId}
                  onChange={(e) => setFilterLayerId(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {scenarioLayers.map((layer) => (
                    <MenuItem key={layer.scenario_layer_id} value={layer.scenario_layer_id}>
                      {layer.layer_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="outlined"
                  onClick={() => setImportModalOpen(true)}
                  disabled={loadingData || scenarioLayers.length === 0}
                >
                  Import CSV
                </Button>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box component="span" sx={sortHeaderSx} onClick={() => handleSort('name')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleSort('name')}>
                        Name {sortBy === 'name' ? (sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />) : <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.7 }} />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={sortHeaderSx} onClick={() => handleSort('reservoir')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleSort('reservoir')}>
                        Reservoir {sortBy === 'reservoir' ? (sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />) : <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.7 }} />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={sortHeaderSx} onClick={() => handleSort('date')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleSort('date')}>
                        Entry Date {sortBy === 'date' ? (sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />) : <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.7 }} />}
                      </Box>
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell width={90}>X km</TableCell>
                    <TableCell width={90}>Y km</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAndSortedWells.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        {wellsData.length === 0 ? 'No wells. Use the button below to add one.' : 'No wells match the current filters.'}
                      </TableCell>
                    </TableRow>
                  ) : filteredAndSortedWells.map((well) => (
                    <TableRow
                      key={well.id}
                      sx={{
                        backgroundColor: well.isInvalid ? 'error.light' : 'inherit',
                        '&:hover': {
                          backgroundColor: well.isInvalid ? 'error.main' : 'action.hover',
                        }
                      }}
                    >
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Enter well name"
                          value={well.well_name}
                          onChange={(e) => handleWellNameChange(well.id, e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel>Reservoir</InputLabel>
                          <Select
                            value={well.scenario_layer_id || ''}
                            label="Reservoir"
                            onChange={(e) => handleLayerChange(well.id, e.target.value)}
                            error={well.isInvalid}
                          >
                            {scenarioLayers.map((layer) => (
                              <MenuItem key={layer.scenario_layer_id} value={layer.scenario_layer_id}>
                                {layer.layer_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DatePicker
                            value={well.entry_date}
                            onChange={(date) => handleEntryDateChange(well.id, date)}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={well.type_id || productionWellTypeId || ''}
                            label="Type"
                            onChange={(e) => handleWellTypeChange(well.id, e.target.value)}
                          >
                            {productionWellTypeId && (
                              <MenuItem value={productionWellTypeId}>Production</MenuItem>
                            )}
                            {injectionWellTypeId && (
                              <MenuItem value={injectionWellTypeId}>Injection</MenuItem>
                            )}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={well.x_km ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setWellsData((prev) =>
                              prev.map((w) =>
                                w.id === well.id
                                  ? { ...w, x_km: v === '' ? null : Number(v) }
                                  : w,
                              ),
                            );
                          }}
                          inputProps={{ step: 0.1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={well.y_km ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setWellsData((prev) =>
                              prev.map((w) =>
                                w.id === well.id
                                  ? { ...w, y_km: v === '' ? null : Number(v) }
                                  : w,
                              ),
                            );
                          }}
                          inputProps={{ step: 0.1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteWell(well.id)}
                          size="small"
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddWell}
                disabled={loadingData || scenarioLayers.length === 0}
                size="large"
              >
                Add Well
              </Button>
            </Box>
          </CardContent>
            </Card>
          )}

          {wellsInnerTab === 'interactive' && (
            <Box sx={{ mb: 4 }}>
              {loadingBaseCurves ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    px: 2,
                    gap: 2,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={44} />
                  <Typography color="text.secondary" align="center">
                    {baseCurvesProgress || 'Loading base well curves for all reservoirs…'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 420 }}>
                    {isManualMode
                      ? 'Loading analogue type-well curves for interactive planning.'
                      : 'Training models per layer can take a while. Please wait.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  {featureWarningMessages.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Custom model feature mismatch
                      </Typography>
                      {featureWarningMessages.map((m) => (
                        <Typography key={m} variant="body2" component="div">
                          {m}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  </Typography>
                  {scenarioLayers.map((layer) => (
                    <LayerDrillingPlanPanel
                      key={layer.scenario_layer_id}
                      layerName={layer.layer_name || layer.scenario_layer_id}
                      unitId={scenarioUnitId}
                      curves={baseCurvesCache?.curves[layer.scenario_layer_id] ?? null}
                      curvesError={baseCurvesCache?.errors[layer.scenario_layer_id] ?? null}
                      plan={interactivePlanByLayerId[layer.scenario_layer_id] ?? createDefaultInteractiveLayerPlan()}
                      onPlanChange={(plan) => handleInteractivePlanChange(layer.scenario_layer_id, plan)}
                    />
                  ))}
                </>
              )}
            </Box>
          )}
        </Box>
      )}

      {SHOW_LEGACY_WELLS_UI && (
        <>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: scenarioLayers.length > 0 ? 2 : 4 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveClick}
          disabled={saving || loadingData}
        >
          {saving ? 'Saving...' : 'Save Wells'}
        </Button>
      </Box>

      {wellsInnerTab === 'interactive' && interactiveDirty && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Interactive changes are pending. They will be applied to the table only after you click Save Wells.
        </Typography>
      )}

      {scenarioLayers.length === 0 && !loadingData && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please create at least one reservoir before adding wells.
        </Alert>
      )}

      <Dialog
        open={interactiveSaveConfirmOpen}
        onClose={() => !saving && setInteractiveSaveConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Replace drilling plan?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Saving from the interactive tab fully replaces existing scenario wells with the current bar schedule.
            Any manual edits in the table will be overwritten by this new plan.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Current plan: {plannedInteractiveWellCount} well{plannedInteractiveWellCount === 1 ? '' : 's'}.
            {savedScenarioWells.length > 0
              ? ` This will remove ${savedScenarioWells.length} existing well${savedScenarioWells.length === 1 ? '' : 's'}.`
              : ''}
          </DialogContentText>
          {plannedInteractiveWellCount === 0 && savedScenarioWells.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              The schedule is empty — saving would delete all existing wells without creating new ones.
              Adjust the bars first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInteractiveSaveConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saving || (plannedInteractiveWellCount === 0 && savedScenarioWells.length > 0)}
            onClick={async () => {
              setInteractiveSaveConfirmOpen(false);
              await handleSaveInteractiveWells();
            }}
          >
            {saving ? 'Saving...' : 'Replace and save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ScenarioWellsImportCsvModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        scenarioLayers={scenarioLayers}
        productionWellTypeId={productionWellTypeId}
        injectionWellTypeId={injectionWellTypeId}
        onImport={handleImportWellsRows}
      />
        </>
      )}

    </Box>
  );
});
